/* ========== SUNBURST CHART ==========
 * Two modes:
 *   - Flat: a single ring of slices (one per value).
 *   - Hierarchical: when series.hierarchy is present, each level becomes
 *     a concentric ring (innermost = top-level parent, outermost = leaf).
 *     Children share their parent's wedge angle, divided proportionally
 *     by their own values.
 * ================================== */

import { COLORS } from './utils';
import { shadeColor } from './utils';

/** Draw one annular slice and optionally label it. */
function drawSlice(ctx, cx, cy, rIn, rOut, a0, a1, color, label) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, rOut, a0, a1);
  ctx.arc(cx, cy, rIn, a1, a0, true);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  if (label && (a1 - a0) > 0.2 && (rOut - rIn) > 14) {
    var mid = (a0 + a1) / 2;
    var lr = (rIn + rOut) / 2;
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, cx + Math.cos(mid) * lr, cy + Math.sin(mid) * lr);
  }
}

export function drawSunburstChart(ctx, centerX, centerY, radius, chartObj) {
  var series = chartObj && chartObj.series && chartObj.series[0] ? chartObj.series[0] : null;
  if (!series || series.data.length === 0) return;
  var data = series.data.map(function (v) { return Math.abs(v); });
  var total = data.reduce(function (a, b) { return a + b; }, 0);
  if (total === 0) return;

  var hierarchy = series.hierarchy || chartObj.hierarchy;

  // ── Hierarchical sunburst ─────────────────────────────────────────────
  if (hierarchy && hierarchy.length > 0 && hierarchy[0].length > 1) {
    // Build a tree: nodes keyed by path
    var root = { children: {}, value: 0, label: '' };
    hierarchy.forEach(function (row, idx) {
      var node = root;
      row.forEach(function (label, lvl) {
        if (!node.children[label]) {
          node.children[label] = { children: {}, value: 0, label, colorIdx: -1 };
        }
        node = node.children[label];
        if (lvl === row.length - 1) node.value += data[idx];
      });
    });
    // Propagate values up
    function sumUp(node) {
      var sum = node.value;
      for (var k in node.children) sum += sumUp(node.children[k]);
      node.value = sum;
      return sum;
    }
    sumUp(root);

    // Detect "degenerate" hierarchy — when every node has exactly one
    // child, the data is really a flat list with composite labels and
    // shouldn't be split into concentric rings. Compute the effective
    // depth by walking the tree and counting only branching nodes.
    function effectiveDepth(node) {
      var maxChildDepth = 0;
      var branchFactor = 0;
      for (var k in node.children) {
        branchFactor++;
        var d = effectiveDepth(node.children[k]);
        if (d > maxChildDepth) maxChildDepth = d;
      }
      // A node only adds to depth if it has children (i.e., isn't a leaf)
      return branchFactor > 0 ? maxChildDepth + 1 : 0;
    }
    function maxBranching(node) {
      var maxB = Object.keys(node.children).length;
      for (var k in node.children) {
        var b = maxBranching(node.children[k]);
        if (b > maxB) maxB = b;
      }
      return maxB;
    }
    var nominalDepth = hierarchy[0].length;
    var fullDepth = effectiveDepth(root);
    var branchAtAnyLevel = maxBranching(root);
    // Total rows ÷ unique top-level branches should be 1 if every parent
    // has exactly one child — that's the degenerate case.
    var topLevelBranches = Object.keys(root.children).length;
    var isDegenerate = topLevelBranches === hierarchy.length;

    // When degenerate, render as a single outer ring with composite labels
    // (the leaf-most label) using the full radius. This matches Excel's
    // behavior for data where each path is unique.
    if (isDegenerate) {
      var innerRadiusFallback = radius * 0.35;
      var currentAngleFlat = -Math.PI / 2;
      var flatTotal = data.reduce(function (a, v) { return a + v; }, 0);
      data.forEach(function (val, i) {
        var sliceAngle = (val / flatTotal) * Math.PI * 2;
        var leafLabel = hierarchy[i][hierarchy[i].length - 1];
        var color = COLORS[i % COLORS.length];
        drawSlice(ctx, centerX, centerY, innerRadiusFallback, radius,
                  currentAngleFlat, currentAngleFlat + sliceAngle, color, leafLabel);
        currentAngleFlat += sliceAngle;
      });
      return;
    }

    var depth = Math.max(1, fullDepth);
    var ringW = radius / depth;

    // Render level by level. Each level's slice angle = parent's angle
    // proportionally divided by child values. Color = parent's color
    // (with slight lumMod variation per child).
    var pIdx = 0;
    function renderNode(node, lvl, a0, a1, baseColor) {
      var children = Object.keys(node.children).map(function (k) { return node.children[k]; });
      if (children.length === 0) return;
      children.sort(function (a, b) { return b.value - a.value; });
      var ang = a0;
      children.forEach(function (child) {
        var slice = (child.value / node.value) * (a1 - a0);
        var color;
        if (lvl === 0) {
          // Top-level wedges each get a distinct palette color
          if (child.colorIdx === -1) child.colorIdx = pIdx++;
          color = COLORS[child.colorIdx % COLORS.length];
        } else {
          // Child tinted slightly lighter than parent
          color = shadeColor(baseColor, 12 * lvl);
        }
        var rIn = lvl * ringW;
        var rOut = (lvl + 1) * ringW;
        drawSlice(ctx, centerX, centerY, rIn, rOut, ang, ang + slice, color, child.label);
        renderNode(child, lvl + 1, ang, ang + slice, color);
        ang += slice;
      });
    }
    renderNode(root, 0, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2, COLORS[0]);
    return;
  }

  // ── Flat sunburst (single ring) ───────────────────────────────────────
  var cats = chartObj.categories || [];
  var pointColors = series.pointColors || [];
  var innerRadius = radius * 0.35;
  var currentAngle = -Math.PI / 2;

  data.forEach(function (val, i) {
    var sliceAngle = (val / total) * Math.PI * 2;
    var color = pointColors[i] || COLORS[i % COLORS.length];
    var label = cats[i] || ('' + (i + 1));
    drawSlice(ctx, centerX, centerY, innerRadius, radius,
              currentAngle, currentAngle + sliceAngle, color, label);
    currentAngle += sliceAngle;
  });
}
